import { useEffect, useState } from "react";
import axiosInstant from "../api";
import { getStaffUsersByType } from "../api/endpoints";
import { cldUrl } from "../utils/cloudinary";

export const ACCOUNT_MANAGER_USERTYPE = "account manager admin";
export const FULFILLMENT_USERTYPE = "post production admin";

export interface StaffOption {
    label: string;
    value: number;
    avatar?: string;
}

export interface StaffOptionsState {
    options: StaffOption[];
    loading: boolean;
    error: boolean;
}

const toStaffOption = (user: any): StaffOption => ({
    label:
        user.displayName ||
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        `User ${user.userid}`,
    value: user.userid,
    avatar: user.avatar ? cldUrl(user.avatar, { w: 44, h: 44, crop: "fill", gravity: "face" }) : ""
});

export const useStaffOptions = (usertype: string): StaffOptionsState => {
    const [state, setState] = useState<StaffOptionsState>({
        options: [],
        loading: true,
        error: false
    });

    useEffect(() => {
        let cancelled = false;
        setState({ options: [], loading: true, error: false });
        axiosInstant
            .get(getStaffUsersByType(usertype))
            .then(({ data }) => {
                const rows = data?.rows || data || [];
                if (cancelled) return;
                setState({
                    options: (Array.isArray(rows) ? rows : []).map(toStaffOption),
                    loading: false,
                    error: false
                });
            })
            .catch((err) => {
                console.error(`Failed to load "${usertype}" staff options:`, err);
                if (cancelled) return;
                setState({ options: [], loading: false, error: true });
            });
        return () => {
            cancelled = true;
        };
    }, [usertype]);

    return state;
};

// One picker fed by several usertype lists (e.g. Project Manager: fulfillment
// and account managers). Loading/error if any source is; options merged,
// de-duplicated and sorted by name.
export const mergeStaffOptions = (...states: StaffOptionsState[]): StaffOptionsState => {
    const byValue = new Map<number, StaffOption>();
    states.forEach((s) => s.options.forEach((o) => byValue.set(o.value, o)));
    return {
        options: [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label)),
        loading: states.some((s) => s.loading),
        error: states.some((s) => s.error)
    };
};

// An empty staff list is indistinguishable from a failed fetch once it reaches
// a <select>, and a select silently rendering no one reads as "there is nobody
// to pick" — so say which it is in the placeholder, and lock the field while
// it is loading or broken rather than letting a save clear the assignment.
export const staffPlaceholder = ({ loading, error }: StaffOptionsState, fallback: string): string => {
    if (loading) return "Loading…";
    if (error) return "Could not load staff — reopen to retry";
    return fallback;
};
